// «Склад → Минусы»: форматы и раскладка данных /data/stock/negative и /data/stock/product-history.
// Количество бэкенд уже округлил по единице (кг, л — 3 знака; шт, порц, г, мл — до целых или 2 знака)
// и убрал шум округления — здесь только запись числа. Суммы — себестоимость минуса, показываем без знака.
import { formatMoney, plural } from '../../Kitchen/kitchenFormat';

export const ALL_STORES = '';
// До стольких складов фильтр — переключателем, больше — выпадающим списком
export const STORE_TABS_MAX = 3;
// Переключатель только для коротких названий: иначе на телефоне он уезжает в прокрутку
export const STORE_TABS_MAX_LABEL = 40;

const MINUS = '−';

const number = (value, digits = 3) =>
  (Number(value) || 0).toLocaleString('ru-RU', { maximumFractionDigits: digits }).replace('-', MINUS);

// «−3,363 кг», «−2 шт»; без единицы — только число
export const formatQuantity = (value, unit) => (unit ? `${number(value)} ${unit}` : number(value));

// Изменение за день: «+0,5», «−1,25», «0»
export function formatChange(value, unit) {
  const text = formatQuantity(value, unit);
  return Number(value) > 0 ? `+${text}` : text;
}

export const formatCost = (value) => formatMoney(Math.abs(Number(value) || 0));

export const positionsText = (count) => `${count} ${plural(count, ['позиция', 'позиции', 'позиций'])}`;

// Итог над списком: «3 позиции в минусе на сумму 2 132 ₽»
export const summaryText = (totals) =>
  `${positionsText(totals.items)} в минусе на сумму ${formatCost(totals.cost_sum)}`;

// «2026-10-25» → «25.10»
export const dayLabel = (iso) => (typeof iso === 'string' ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : '');

// Склады выбранного фильтра и итог по ним (фильтр — на устройстве: ответ бэкенда уже со всеми складами)
export function visibleStores(data, storeId) {
  const stores = (data?.stores || []).filter((store) => !storeId || store.id === storeId);
  const totals = stores.reduce(
    (sum, store) => ({
      items: sum.items + store.items,
      cost_sum: sum.cost_sum + store.cost_sum,
      no_price: sum.no_price + (store.no_price || 0),
    }),
    { items: 0, cost_sum: 0, no_price: 0 }
  );
  return { stores, totals };
}

// «из них 20 без цены» — рядом с числом позиций; без цены нет — пусто
export const noPriceText = (totals) => (totals?.no_price > 0 ? `из них ${totals.no_price} без цены` : '');

// Позиции без цены (не было приходов на склад — iiko списывает их в блюда по нулевой цене) — отдельной группой
// первой в складе; из обычных групп они убираются (опустевшая группа не показывается)
export const NO_PRICE_GROUP = 'no-price';

export function withNoPriceGroup(store) {
  const noPrice = store.groups.flatMap((group) => group.products.filter((p) => p.no_price));
  const groups = store.groups
    .map((group) => {
      const products = group.products.filter((p) => !p.no_price);
      return products.length === group.products.length
        ? group
        : { ...group, products, items: products.length, cost_sum: products.reduce((s, p) => s + p.cost_sum, 0) };
    })
    .filter((group) => group.products.length > 0);
  if (noPrice.length === 0) return store;
  return {
    ...store,
    groups: [
      {
        id: NO_PRICE_GROUP,
        name: 'Не было приходов на склад',
        note: 'себестоимость блюд с этими товарами занижена',
        no_price_group: true,
        products: noPrice,
        items: noPrice.length,
        cost_sum: 0,
      },
      ...groups,
    ],
  };
}

// Норма на единицу блюда: кг → г и л → мл, если меньше 1 («12,9 г»); иначе как остаток
const SMALLER_UNIT = { кг: 'г', л: 'мл' };

export function formatNorm(value, unit) {
  const norm = Math.abs(Number(value) || 0);
  const smaller = SMALLER_UNIT[(unit || '').trim().toLowerCase().replace(/\.$/, '')];
  if (smaller && norm < 1) {
    return `${(norm * 1000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ${smaller}`;
  }
  return formatQuantity(norm, unit);
}

// «Используется в 85 блюдах»
export const dishesTitle = (count) => `Используется в ${count} ${plural(count, ['блюде', 'блюдах', 'блюдах'])}`;

// «по техкартам за 7 дней — 1,262 кг из 1,096 кг фактического расхода»; расхода не было — только по техкартам
export function usageSummary(data, days) {
  const byCharts = `по техкартам за ${days} ${plural(days, ['день', 'дня', 'дней'])} — ${formatQuantity(data.total_by_charts, data.unit)}`;
  return Number(data.actual_consumption) > 0
    ? `${byCharts} из ${formatQuantity(data.actual_consumption, data.unit)} фактического расхода`
    : byCharts;
}

// Блюда с продажами за период — списком (по «ушло»), без продаж — отдельно
export function splitDishes(dishes) {
  return {
    sold: (dishes || []).filter((d) => Number(d.sold) > 0),
    unsold: (dishes || []).filter((d) => !(Number(d.sold) > 0)),
  };
}

const HOUR_MS = 60 * 60 * 1000;

// Строка на «Обзоре»: «На складах 156 позиций в минусе на 1 738 101 ₽, из них 20 без цены — себестоимость может быть
// посчитана неверно» и «на HH:MM», если сводке больше часа. Минусов нет или данных нет — null (строки нет)
export function overviewLine(summary, now = new Date()) {
  if (!summary?.available || !(summary.items > 0)) return null;
  const noPrice = summary.no_price > 0 ? `, ${noPriceText(summary)}` : '';
  const at = summary.updated_at ? new Date(summary.updated_at) : null;
  const old = at && !Number.isNaN(at.getTime()) && now - at > HOUR_MS;
  const time = old ? ` (на ${at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })})` : '';
  return `На складах ${positionsText(summary.items)} в минусе на ${formatCost(summary.cost_sum)}${noPrice} — себестоимость может быть посчитана неверно${time}`;
}

// Склад из фильтра пропал из ответа (минусов там больше нет) — показываем все
export const effectiveStore = (data, storeId) =>
  storeId && (data?.store_options || []).some((store) => store.id === storeId) ? storeId : ALL_STORES;

export function storeFilterKind(options) {
  if (options.length < 2) return 'none';
  const short = options.reduce((sum, option) => sum + option.name.length, 0) <= STORE_TABS_MAX_LABEL;
  return options.length <= STORE_TABS_MAX && short ? 'tabs' : 'menu';
}

// Линия остатка на конец дня. Координаты — проценты (0–100) ширины и высоты графика.
// Шкала Y — по диапазону остатков с запасом 15 %, не от нуля; zero — высота нулевой линии, null — ноль вне диапазона.
// Точка дня — в середине его колонки (как подпись даты под осью).
// segments — куски линии одного знака: на переходе через ноль линия делится в точке пересечения (crossings),
// чтобы плюс рисовать серым, а минус — красным
export function lineModel(input) {
  const values = input.map((value) => Number(value) || 0);
  const count = values.length;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const pad = span > 0 ? span * 0.15 : Math.max(Math.abs(max) * 0.1, 1);
  const lo = min - pad;
  const hi = max + pad;
  const y = (value) => ((hi - value) / (hi - lo)) * 100;
  const x = (index) => ((index + 0.5) / count) * 100;
  const zero = lo < 0 && hi > 0 ? y(0) : null;

  const points = values.map((value, index) => ({ x: x(index), y: y(value), negative: value < 0 }));
  const segments = [];
  const crossings = [];
  let current = null;
  values.forEach((value, index) => {
    const point = [x(index), y(value)];
    const negative = value < 0;
    if (!current) {
      current = { negative, points: [point] };
      return;
    }
    if (current.negative !== negative) {
      const previous = values[index - 1];
      const share = previous / (previous - value);
      const cross = [x(index - 1) + share * (x(index) - x(index - 1)), y(0)];
      current.points.push(cross);
      segments.push(current);
      crossings.push({ x: cross[0], y: cross[1], index, toNegative: negative });
      current = { negative, points: [cross, point] };
    } else {
      current.points.push(point);
    }
  });
  if (current) segments.push(current);
  return { zero, points, segments, crossings };
}
